import { ArrowUpRight, ArrowRight, ArrowLeft } from "lucide-react";
import { Link } from "react-router-dom";
import { useLang } from "../contexts/LanguageContext";
import RoadVisual from "./RoadVisual";

export default function Hero() {
  const { t, dir } = useLang();
  const ArrowIcon = dir === "rtl" ? ArrowLeft : ArrowRight;

  return (
    <section
      id="home"
      className="relative min-h-[100svh] w-full flex items-center pt-24 pb-20 overflow-hidden"
      data-testid="hero-section"
    >
      <RoadVisual />

      {/* content */}
      <div className="relative max-w-7xl mx-auto px-6 sm:px-10 w-full">
        <div className="max-w-3xl">
          <p
            className="font-mono-tech text-[#00F0FF] text-xs sm:text-sm tracking-[0.3em] uppercase mb-6 wt-rise"
            data-testid="hero-eyebrow"
          >
            <span className="inline-block w-8 h-px bg-[#00F0FF] align-middle me-3" />
            {t.hero.eyebrow}
          </p>

          <h1
            className="font-display text-white text-[44px] leading-[1.02] sm:text-6xl lg:text-7xl xl:text-[88px] font-bold tracking-tight wt-rise"
            style={{ animationDelay: "120ms" }}
            data-testid="hero-headline"
          >
            {t.hero.headline_1}{" "}
            <span className="text-[#00F0FF] wt-glow-text">
              {t.hero.headline_2}
            </span>
            <br />
            {t.hero.headline_3}
          </h1>

          <p
            className="mt-7 text-slate-200 text-base sm:text-lg lg:text-xl max-w-2xl leading-[1.7] wt-rise"
            style={{ animationDelay: "240ms" }}
            data-testid="hero-sub"
          >
            {t.hero.sub}
          </p>

          <div
            className="mt-10 flex flex-wrap gap-4 wt-rise"
            style={{ animationDelay: "320ms" }}
          >
            <Link
              to="/contact"
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-3 bg-[#00F0FF] text-[#050B14] hover:bg-white transition-colors px-7 sm:px-8 py-4 text-xs sm:text-sm font-bold uppercase tracking-[0.18em]"
            >
              {t.hero.cta_primary}
              <ArrowUpRight
                size={18}
                className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </Link>
            <Link
              to="/services"
              data-testid="hero-cta-secondary"
              className="group inline-flex items-center gap-3 border border-[#00F0FF] text-[#00F0FF] hover:bg-[rgba(0,240,255,0.1)] transition-colors px-7 sm:px-8 py-4 text-xs sm:text-sm font-bold uppercase tracking-[0.18em]"
            >
              {t.hero.cta_secondary}
              <ArrowIcon
                size={18}
                className="transition-transform group-hover:translate-x-1 rtl:group-hover:-translate-x-1"
              />
            </Link>
          </div>

          {/* stats */}
          <div
            className="mt-16 grid grid-cols-3 gap-6 sm:gap-12 max-w-2xl wt-rise"
            style={{ animationDelay: "420ms" }}
            data-testid="hero-stats"
          >
            <Stat n="08+" label={t.hero.stat_1} />
            <Stat n="120+" label={t.hero.stat_2} />
            <Stat n="99.9%" label={t.hero.stat_3} />
          </div>
        </div>
      </div>

      {/* bottom edge accent */}
      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-[#00F0FF]/40 to-transparent" />
    </section>
  );
}

function Stat({ n, label }) {
  return (
    <div className="border-t border-[rgba(0,240,255,0.18)] pt-4">
      <div className="font-display text-3xl sm:text-4xl font-bold text-white">
        {n}
      </div>
      <div className="font-mono-tech text-xs uppercase tracking-[0.18em] text-slate-400 mt-1">
        {label}
      </div>
    </div>
  );
}
